import React, { useState } from 'react';
import { 
    AppBar, 
    Toolbar, 
    Box, 
    Button, 
    Typography, 
    IconButton, 
    Menu, 
    MenuItem, 
    useTheme, 
    useMediaQuery,
    Tooltip
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MenuIcon from '@mui/icons-material/Menu';

function Header() {
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const isDark = theme.palette.mode === 'dark';

    const handleMobileMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleLogoClick = () => {
        if (location.pathname === '/about') {
            const token = localStorage.getItem('authToken');
            const email = localStorage.getItem('email');
            
            console.log('Header - Logo Click:', {
                token,
                email,
                allLocalStorage: { ...localStorage },
                location: location.pathname,
                authDetails: {
                    token: token?.substring(0, 20) + '...',
                    email: email,
                    hasToken: !!token,
                    hasEmail: !!email
                }
            });
            
            if (token) {
                localStorage.setItem('authToken', token);
            }
            navigate('/');
        }
    };

    const AppBadge = ({ icon: Icon, label, tooltip }) => (
        <Tooltip title={tooltip} arrow placement="bottom">
            <Box 
                sx={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '20px',
                    backgroundColor: isDark 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(15, 23, 42, 0.04)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.07)'}`,
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    cursor: 'default',
                    userSelect: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: isDark 
                            ? 'rgba(255, 255, 255, 0.08)' 
                            : 'rgba(15, 23, 42, 0.07)',
                        color: theme.palette.text.primary,
                    }
                }}
            >
                <Icon sx={{ fontSize: 16, opacity: 0.85 }} />
                <span>{label}</span>
            </Box>
        </Tooltip>
    );

    const DesktopMenu = () => (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5
        }}>
            <AppBadge 
                icon={AppleIcon} 
                label="iOS App Soon" 
                tooltip="iOS application is in development"
            />
            
            <AppBadge 
                icon={AndroidIcon} 
                label="Android App Soon" 
                tooltip="Android application is in development"
            />

            <Button
                component={RouterLink}
                to={location.pathname === '/about' ? '/' : '/about'}
                startIcon={location.pathname === '/about' 
                    ? <ArrowBackRoundedIcon sx={{ fontSize: 18 }} /> 
                    : <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                }
                sx={{ 
                    borderRadius: '20px',
                    px: 2,
                    py: 0.55,
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: location.pathname === '/about' ? theme.palette.primary.main : theme.palette.text.primary,
                    backgroundColor: location.pathname === '/about' 
                        ? (isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.08)')
                        : 'transparent',
                    border: `1px solid ${location.pathname === '/about' 
                        ? theme.palette.primary.main 
                        : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.09)')
                    }`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 102, 255, 0.06)',
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        transform: 'translateY(-1px)',
                    }
                }}
            >
                {location.pathname === '/about' ? 'Back to Home' : 'About'}
            </Button>
        </Box>
    );

    const MobileMenu = () => (
        <>
            <IconButton
                color="inherit"
                onClick={handleMobileMenuOpen}
                sx={{ 
                    ml: 'auto',
                    p: 1,
                    borderRadius: '10px',
                    color: theme.palette.text.primary,
                    '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                    }
                }}
            >
                <MenuIcon />
            </IconButton>
            
            <Menu
                anchorEl={mobileMenuAnchor}
                open={Boolean(mobileMenuAnchor)}
                onClose={handleMobileMenuClose}
                onClick={handleMobileMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: '210px',
                        mt: 1,
                        borderRadius: '16px',
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: isDark 
                            ? '0 16px 36px rgba(0, 0, 0, 0.6)' 
                            : '0 16px 36px rgba(15, 23, 42, 0.12)',
                        p: 0.5,
                        '& .MuiMenuItem-root': {
                            borderRadius: '10px',
                            py: 1,
                            px: 1.5,
                            my: 0.25,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 102, 255, 0.08)',
                                color: theme.palette.primary.main,
                            }
                        }
                    }
                }}
            >
                <MenuItem 
                    disabled
                    sx={{ 
                        color: `${theme.palette.text.secondary} !important`,
                        opacity: '0.7 !important'
                    }}
                >
                    <span>iOS App Soon</span>
                    <AppleIcon sx={{ fontSize: 18 }} />
                </MenuItem>
                <MenuItem 
                    disabled
                    sx={{ 
                        color: `${theme.palette.text.secondary} !important`,
                        opacity: '0.7 !important'
                    }}
                >
                    <span>Android App Soon</span>
                    <AndroidIcon sx={{ fontSize: 18 }} />
                </MenuItem>
                <MenuItem 
                    component={RouterLink}
                    to={location.pathname === '/about' ? '/' : '/about'}
                    sx={{
                        color: location.pathname === '/about' ? theme.palette.primary.main : theme.palette.text.primary,
                        fontWeight: 600
                    }}
                >
                    <span>{location.pathname === '/about' ? 'Back to Home' : 'About MyTownCode'}</span>
                    {location.pathname === '/about' 
                        ? <ArrowBackRoundedIcon sx={{ fontSize: 18 }} /> 
                        : <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                    }
                </MenuItem>
            </Menu>
        </>
    );

    return (
        <AppBar 
            position="fixed"
            elevation={0}
            sx={{ 
                backgroundColor: isDark ? 'rgba(11, 15, 25, 0.85)' : 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                boxShadow: 'none',
                borderBottom: `1px solid ${theme.palette.divider}`,
                height: { xs: '60px', sm: '68px' },
                justifyContent: 'center',
                zIndex: theme.zIndex.drawer + 1,
                transition: 'background-color 0.3s ease, border-color 0.3s ease'
            }}
        >
            <Toolbar sx={{ 
                px: { xs: 2, sm: 3, md: 4 }, 
                maxWidth: '1400px', 
                width: '100%', 
                mx: 'auto',
                boxSizing: 'border-box'
            }}>
                {/* Left side - Logo and Brand Name */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        cursor: location.pathname === '/about' ? 'pointer' : 'pointer',
                        flexGrow: 1,
                        textDecoration: 'none',
                        userSelect: 'none',
                        transition: 'opacity 0.2s ease',
                        '&:hover': {
                            opacity: 0.9
                        }
                    }}
                    onClick={() => {
                        if (location.pathname === '/about') {
                            handleLogoClick();
                        } else {
                            navigate('/');
                        }
                    }}
                >
                    <Box 
                        sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1.25,
                            p: 0.25,
                            borderRadius: '10px',
                            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            border: `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <img 
                            src="/logo192.png" 
                            alt="MyTownCode Logo" 
                            style={{ 
                                height: '28px',
                                width: '28px',
                                display: 'block',
                                borderRadius: '6px'
                            }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                color: theme.palette.text.primary,
                                lineHeight: 1
                            }}
                        >
                            MyTownCode
                        </Typography>
                        <Typography 
                            component="span" 
                            sx={{ 
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                color: theme.palette.primary.main,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                display: { xs: 'none', sm: 'inline-block' },
                                backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 102, 255, 0.08)',
                                px: 0.75,
                                py: 0.2,
                                borderRadius: '6px',
                                ml: 0.5
                            }}
                        >
                            Live
                        </Typography>
                    </Box>
                </Box>

                {/* Right side - Navigation Links */}
                {isMobile ? <MobileMenu /> : <DesktopMenu />}
            </Toolbar>
        </AppBar>
    );
}

export default Header;